/**
 * Google Workspace / Google Meet notes seam.
 *
 * Same pattern as `argoWorker.ts` and `enrich.ts`: when
 * `VITE_GSUITE_API_URL` is set we POST the context to a hosted
 * Workspace worker (which owns the OAuth + Calendar/Meet/Gmail pulls)
 * and return the meeting notes it found. When it's NOT set — local
 * dev, demos — `isConfigured()` is false and `pullMeetingNotes` falls
 * back to a *deterministic local simulation* that synthesises a
 * believable set of recent Meet notes for the contact / account so the
 * "Sync Google Meet notes" action is fully usable offline.
 *
 * No secrets in the client. Real OAuth + Calendar/Meet access lives in
 * the hosted worker behind `VITE_GSUITE_API_URL`.
 *
 *   POST {base}/meet-notes  { name?, company?, accountName?, email? }
 *                           → { notes: MeetNote[] }
 */

const BASE_URL: string | undefined = (
  import.meta.env.VITE_GSUITE_API_URL as string | undefined
)?.replace(/\/+$/, '');
const API_KEY = import.meta.env.VITE_GSUITE_API_KEY as string | undefined;

const TIMEOUT_MS = 30_000;

/** True when a real Google Workspace endpoint is configured. */
export function isConfigured(): boolean {
  return Boolean(BASE_URL);
}

/** Backend descriptor for status pills / debugging. */
export function meetInfo(): { configured: boolean; baseUrl: string | null } {
  return { configured: isConfigured(), baseUrl: BASE_URL ?? null };
}

/** Who/what we're pulling meeting notes for. */
export interface MeetContext {
  /** Contact / person name, when syncing from a contact card. */
  name?: string;
  /** Company on the contact, used to title the meeting. */
  company?: string;
  /** Account name, when syncing from an account detail view. */
  accountName?: string;
  /** Contact email — helps the real worker match calendar attendees. */
  email?: string;
}

/** A single Google Meet note mapped to an activity-timeline entry. */
export interface MeetNote {
  id: string;
  /** Meeting title, e.g. "Discovery call — Acme x Argo". */
  title: string;
  /** ISO-8601 UTC start time. */
  at: string;
  durationMins: number;
  attendees: string[];
  /** One-paragraph recap. */
  summary: string;
  /** Extracted action items / next steps. */
  actionItems: string[];
  /** Always "Google Meet" today. */
  source: string;
}

export class MeetNotesError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'MeetNotesError';
  }
}

/**
 * Pull recent Google Meet notes for a contact / account. Never throws —
 * a worker/network failure downgrades to the local simulation so the
 * action always produces notes.
 */
export async function pullMeetingNotes(context: MeetContext): Promise<MeetNote[]> {
  if (isConfigured()) {
    try {
      return await postMeetNotes(context);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo gsuite] worker failed, using local simulation:', err);
    }
  }
  return simulateMeetingNotes(context);
}

/**
 * Format a meeting note into a single activity-timeline line. Used by
 * both the contact (`logPersonActivity`) and account (`appendAudit`)
 * write paths so notes read consistently wherever they land.
 */
export function formatMeetNote(note: MeetNote): string {
  const when = new Date(note.at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const actions =
    note.actionItems.length > 0
      ? ` · Next: ${note.actionItems.join('; ')}`
      : '';
  // Carry the simulated marker into the persisted line so a synthesized note
  // is never mistaken for a real meeting once it lands on a timeline.
  const sim = /simulated/i.test(note.source) ? ' _(simulated)_' : '';
  return `${note.title} (${when}, ${note.durationMins}m) — ${note.summary}${actions}${sim}`;
}

async function postMeetNotes(context: MeetContext): Promise<MeetNote[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${BASE_URL}/meet-notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
      },
      body: JSON.stringify(context),
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new MeetNotesError(
        `GSuite worker responded ${res.status} ${res.statusText}`,
      );
    }
    const raw = (await res.json()) as { notes?: unknown };
    const notes = Array.isArray(raw.notes) ? raw.notes : [];
    return notes
      .map(coerceNote)
      .filter((n): n is MeetNote => n !== null);
  } finally {
    clearTimeout(timer);
  }
}

/** Defensively coerce an untrusted worker payload into a MeetNote. */
function coerceNote(value: unknown): MeetNote | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const title = typeof v.title === 'string' ? v.title : undefined;
  const summary = typeof v.summary === 'string' ? v.summary : undefined;
  if (!title || !summary) return null;
  return {
    id: typeof v.id === 'string' ? v.id : crypto.randomUUID(),
    title,
    at: typeof v.at === 'string' ? v.at : new Date().toISOString(),
    durationMins: typeof v.durationMins === 'number' ? v.durationMins : 30,
    attendees: Array.isArray(v.attendees)
      ? v.attendees.filter((a): a is string => typeof a === 'string')
      : [],
    summary,
    actionItems: Array.isArray(v.actionItems)
      ? v.actionItems.filter((a): a is string => typeof a === 'string')
      : [],
    source: typeof v.source === 'string' ? v.source : 'Google Meet',
  };
}

// ---------------------------------------------------------------------------
// Deterministic local simulation
// ---------------------------------------------------------------------------

/** Stable 0–1 hash so the same context always reads the same way. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

const MEETING_KINDS = [
  {
    title: 'Discovery call',
    summary:
      'Walked through current workflow pain and where automation could remove manual steps. Strong interest in the agent layer; budget owner looped in.',
    actions: ['Send recap + tailored ROI estimate', 'Schedule technical deep-dive'],
    duration: 30,
  },
  {
    title: 'Technical deep-dive',
    summary:
      'Reviewed integration surface and data flow. Their eng lead asked about security posture and SLA; no blockers surfaced.',
    actions: ['Share security one-pager', 'Confirm pilot scope'],
    duration: 45,
  },
  {
    title: 'Pricing & proposal review',
    summary:
      'Walked the proposal line-by-line. Pricing landed well; they want a short pilot before annual commit.',
    actions: ['Draft pilot SOW', 'Align on success metrics'],
    duration: 30,
  },
  {
    title: 'Quarterly check-in',
    summary:
      'Reviewed adoption and outcomes to date. Usage trending up; identified two expansion opportunities in adjacent teams.',
    actions: ['Intro to adjacent team lead', 'Prep expansion deck'],
    duration: 30,
  },
];

const ATTENDEE_POOL = ['Trent Hughes', 'Grant', 'Patrick', 'Adam'];

function simulateMeetingNotes(context: MeetContext): MeetNote[] {
  const subject = context.accountName ?? context.company ?? context.name ?? 'Prospect';
  const counterpart = context.name ?? `${subject} team`;
  const seed = `${subject}|${context.name ?? ''}|${context.email ?? ''}`;
  const r = hash01(seed);

  // 1–2 recent meetings, deterministic from the hash.
  const count = 1 + (Math.floor(r * 100) % 2);
  const startIdx = Math.floor(r * MEETING_KINDS.length);
  const notes: MeetNote[] = [];

  for (let i = 0; i < count; i++) {
    const kind = MEETING_KINDS[(startIdx + i) % MEETING_KINDS.length];
    // Spread meetings back over the last few weeks.
    const daysAgo = 4 + i * 9 + Math.floor(r * 6);
    const at = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
    const argoAttendee = ATTENDEE_POOL[(startIdx + i) % ATTENDEE_POOL.length];
    notes.push({
      id: crypto.randomUUID(),
      title: `${kind.title} — ${subject} x Argo`,
      at,
      durationMins: kind.duration,
      attendees: [argoAttendee, counterpart],
      summary: kind.summary,
      actionItems: kind.actions,
      // Marked so the persisted timeline line reads "(simulated)" — these are
      // synthesized, not pulled from a real calendar. Self-clears once the
      // GSuite worker is wired (real notes come back source: "Google Meet").
      // See AUDIT.md §4B.
      source: 'Google Meet (simulated)',
    });
  }

  // Most-recent-first for display.
  notes.sort((a, b) => b.at.localeCompare(a.at));
  return notes;
}
